import { CalendarDays, Headset, IdCard, Laptop, Monitor, Smartphone, Tablet } from "lucide-react";

/**
 * The six animated pictures on the sign-in carousel. Each one is drawn in CSS (`.hx-*` in
 * tokens.css), so it runs whenever its slide is on screen and stops with the rest of the page.
 * Colours come from the theme variables in tokens.css, so the pictures follow light and dark.
 */

export function EmployeesVisual() {
  return (
    <svg width="250" height="156" viewBox="0 0 250 156" aria-hidden="true">
      <g className="hx-flow hx-tree" fill="none" strokeWidth="1.5" strokeLinecap="round">
        <path d="M125 29 V46 H45 V63 M125 46 H205 V63 M125 46 V63" />
        <path d="M45 77 V94 H25 V112 M45 94 H65 V112" />
        <path d="M125 77 V94 H105 V112 M125 94 H145 V112" />
        <path d="M205 77 V94 H185 V112 M205 94 H225 V112" />
      </g>
      <circle className="hx-nd hx-a0 hx-node-top" cx="125" cy="20" r="9" />
      <circle className="hx-nd hx-a0 hx-node-halo" cx="125" cy="20" r="14" fill="none" />
      <circle className="hx-nd hx-a1 hx-node-mid" cx="45" cy="70" r="7" />
      <circle className="hx-nd hx-a2 hx-node-mid" cx="125" cy="70" r="7" />
      <circle className="hx-nd hx-a3 hx-node-mid" cx="205" cy="70" r="7" />
      <circle className="hx-nd hx-a4 hx-node-low" cx="25" cy="120" r="5.5" />
      <circle className="hx-nd hx-a5 hx-node-low" cx="65" cy="120" r="5.5" />
      <circle className="hx-nd hx-a6 hx-node-low" cx="105" cy="120" r="5.5" />
      <circle className="hx-nd hx-a7 hx-node-low" cx="145" cy="120" r="5.5" />
      <circle className="hx-nd hx-a8 hx-node-low" cx="185" cy="120" r="5.5" />
      <circle className="hx-nd hx-a9 hx-node-low" cx="225" cy="120" r="5.5" />
    </svg>
  );
}

const WEEK = [
  { day: "M", mark: "P", tone: "hx-g" },
  { day: "T", mark: "P", tone: "hx-g" },
  { day: "W", mark: "L", tone: "hx-b" },
  { day: "T", mark: "P", tone: "hx-g" },
  { day: "F", mark: "P", tone: "hx-g" },
];

export function AttendanceVisual() {
  return (
    <div className="hx-vbox">
      <div className="hx-vtop">
        <span>This week</span>
        <span className="hx-vchip hx-g">
          <u />
          Checked in
        </span>
      </div>
      <div className="hx-v2days">
        {WEEK.map(({ day, mark, tone }, i) => (
          <span key={i} className={`hx-dp ${tone}`}>
            <i>{day}</i>
            <b>{mark}</b>
          </span>
        ))}
      </div>
      <div>
        <span className="hx-trk">
          <i className="hx-fill" />
        </span>
        <span className="hx-lbl">
          <em>09:00</em>
          <em>18:00</em>
        </span>
      </div>
    </div>
  );
}

export function LeaveVisual() {
  return (
    <div className="hx-vbox hx-v3">
      <svg width="84" height="84" viewBox="0 0 84 84" aria-hidden="true">
        <circle className="hx-ring-track" cx="42" cy="42" r="33" fill="none" strokeWidth="8" />
        <circle
          className="hx-ringP"
          cx="42"
          cy="42"
          r="33"
          fill="none"
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray="207"
          transform="rotate(-90 42 42)"
        />
        <CalendarDays className="hx-ico" x={30} y={30} size={24} aria-hidden="true" />
      </svg>
      <ul className="hx-steps">
        <li>
          <i />
          Requested
        </li>
        <li>
          <i />
          Approved
        </li>
        <li>
          <i />
          Balance updated
        </li>
      </ul>
    </div>
  );
}

export function PayrollVisual() {
  return (
    <div className="hx-vbox hx-v4">
      <div className="hx-slipHead">
        <b />
        <i />
      </div>
      {[0, 1, 2, 3].map((row) => (
        <div key={row} className="hx-srow">
          <i />
          <em />
        </div>
      ))}
      <div className="hx-stot">
        <span />
        <span className="hx-paychip">
          <b className="pa">Discrepancies</b>
          <b className="pb">Verified</b>
        </span>
      </div>
    </div>
  );
}

export function PerformanceVisual() {
  return (
    <svg width="250" height="156" viewBox="0 0 250 156" aria-hidden="true">
      <defs>
        <linearGradient id="hxPerfGrad" x1="0" y1="0" x2="0" y2="1">
          <stop className="hx-perf-stop-top" offset="0" />
          <stop className="hx-perf-stop-bottom" offset="1" />
        </linearGradient>
      </defs>
      <g className="hx-grid" strokeWidth="1">
        <path d="M14 30H236 M14 62H236 M14 94H236 M14 126H236" />
      </g>
      <path className="hx-goal" d="M14 56H236" strokeWidth="1.2" strokeDasharray="4 4" />
      <path
        className="hx-area"
        d="M14 124 C46 122 62 106 92 108 S140 88 168 84 S214 52 236 38 V140 H14 Z"
        fill="url(#hxPerfGrad)"
      />
      <path
        className="hx-line hx-perf-line"
        pathLength={1}
        d="M14 124 C46 122 62 106 92 108 S140 88 168 84 S214 52 236 38"
        fill="none"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <circle className="hx-endDot hx-end-dot" cx="236" cy="38" r="4.6" />
      <circle className="hx-endRing hx-end-ring" cx="236" cy="38" r="4.6" fill="none" strokeWidth="1.4" />
      <text className="hx-goal-label" x="14" y="18" fontSize={10} fontFamily="DM Sans, sans-serif">
        Goal
      </text>
    </svg>
  );
}

const DEVICES = [Laptop, Smartphone, Monitor, IdCard, Headset, Tablet];

export function AssetsVisual() {
  return (
    <div className="hx-vbox hx-v6">
      <div className="hx-agrid">
        {DEVICES.map((Device, i) => (
          <span key={i} className="hx-dev">
            <Device size={20} strokeWidth={1.7} aria-hidden="true" />
            <em />
          </span>
        ))}
      </div>
      <span className="hx-scanline" />
      <div className="hx-vlegend">
        <span>
          <u className="hx-g" />
          Assigned
        </span>
        <span>
          <u className="hx-b" />
          Available
        </span>
        <span>
          <u className="hx-a" />
          Returned
        </span>
      </div>
    </div>
  );
}
